/**
 * TASK-837 — an advisor can correct a value tag on one client's holding, and say why.
 *
 * TASK-827 made the tag's basis visible: it is a GICS industry-group default, and Tyson Foods
 * was reported as breaching *"No luxury conglomerates"* on that basis alone. Seeing it is the
 * first half; this is the second.
 *
 * ## Why removing a tag asks for a reason and adding one does not
 *
 * **One tag is a switch, not a nudge.** `_score_holding` short-circuits: a single exclusion hit
 * takes the holding to 0.0 however many tilts it matches. And the same tag is read on both
 * sides of the engine — on a held position it raises the conflict, on a candidate it is what
 * the exclusion filter rejects on.
 *
 * So **removing** it suppresses a suitability signal AND unblocks replacements the engine was
 * refusing to propose. That is the direction a regulator asks about, and it carries a required
 * rationale. **Adding** one is an advisor being more careful about their own client: same
 * mechanism, same attribution, no defence required.
 *
 * The gate is enforced by the server and by a CHECK constraint behind it. This component asks
 * for the reason; it does not decide the rule.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';

import { portfolioFitService, type TagProvenance } from '../../services/portfolioFitService';

export function TagCorrection({
  clientId,
  isin,
  tag,
  provenance,
}: {
  clientId: string;
  isin: string | null;
  tag: string;
  provenance?: TagProvenance;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);

  const overridden = provenance?.source === 'advisor_override';

  const invalidate = () => {
    // The correction changes the fit score, the conflict list AND which candidates clear the
    // exclusions. Every one of those is a separate query; refetching only `portfolio-fit`
    // would leave the swap list contradicting the conflict it answers.
    queryClient.invalidateQueries({ queryKey: ['portfolio-fit', clientId] });
    queryClient.invalidateQueries({ queryKey: ['cio-candidates'] });
    queryClient.invalidateQueries({ queryKey: ['tag-overrides', clientId] });
  };

  const remove = useMutation({
    mutationFn: () =>
      portfolioFitService.overrideTag(clientId, {
        isin: isin ?? '', tag, direction: 'remove', rationale,
      }),
    onSuccess: () => { setOpen(false); setRationale(''); setError(null); invalidate(); },
    onError: (e: unknown) => {
      // The server's refusal, shown rather than swallowed — the TASK-732 contract.
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'The server refused this correction.');
    },
  });

  const revert = useMutation({
    mutationFn: () =>
      portfolioFitService.revertTagOverride(clientId, provenance?.override_id ?? ''),
    onSuccess: () => { setError(null); invalidate(); },
    onError: () => setError('The server refused to withdraw this correction.'),
  });

  if (!isin) {
    // Without an ISIN there is nothing to key a correction on. Say nothing rather than offer
    // a control that would fail — the holding's identity is the engine's, not this widget's.
    return null;
  }

  if (overridden) {
    return (
      <div data-testid="tag-correction-active" className="text-[11px] text-muted-foreground">
        {provenance?.rationale && (
          <span data-testid="tag-correction-rationale">&ldquo;{provenance.rationale}&rdquo; · </span>
        )}
        <button
          type="button"
          data-testid="tag-correction-revert"
          disabled={revert.isPending}
          onClick={() => revert.mutate()}
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground disabled:opacity-40"
        >
          <Undo2 className="h-3 w-3" aria-hidden /> Withdraw this correction
        </button>
        {error && <p data-testid="tag-correction-error" className="mt-1 text-destructive">{error}</p>}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        data-testid="tag-correction-open"
        onClick={() => setOpen(true)}
        className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        This tag is wrong for {isin}
      </button>
    );
  }

  return (
    <div data-testid="tag-correction-form" className="space-y-1.5 rounded-md border border-border bg-accent/40 p-2">
      <label className="block text-[11px] text-muted-foreground" htmlFor={`why-${tag}`}>
        {/* Not "why do you disagree" — the record has to say what it rests on, because this
            correction is what a later reader will find instead of the sector default. */}
        Removing <span className="font-medium text-foreground">{tag}</span> clears this conflict
        and lets the engine propose this holding again. What is it about {isin} that the sector
        default gets wrong?
      </label>
      <textarea
        id={`why-${tag}`}
        data-testid="tag-correction-input"
        rows={2}
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        className="w-full rounded border border-border bg-background p-1.5 text-[12px]"
      />
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="tag-correction-confirm"
          disabled={!rationale.trim() || remove.isPending}
          onClick={() => remove.mutate()}
          className="rounded bg-[#002B45] px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
        >
          Record this correction
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-accent"
        >
          Cancel
        </button>
      </div>
      {error && <p data-testid="tag-correction-error" className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

export default TagCorrection;
